
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../../config';

const UserSignUp = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    userName: '',
    firstName: '',
    lastName: '',
    systemPassword: '',
    systemConfirmPassword: '',
    email: '',
    mobileNumber: '',
    address: '',
    countryId: '',
    stateId: '',
  });

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ type: '', text: '' });

  // fetch countries
  useEffect(() => {
    axios
      .post(`${API_BASE_URL}/public/getCountry`, {})
      .then((res) => {
        const data = res?.data;
        setCountries(Array.isArray(data) ? data : data?.countries || []);
      })
      .catch((err) => {
        console.error('Error fetching countries:', err);
        setCountries([]);
      });
  }, []);

  // fetch states when country changes
  useEffect(() => {
    if (formData.countryId) {
      axios
        .post(`${API_BASE_URL}/public/getState`, { countryIds: [formData.countryId] })
        .then((res) => {
          const data = res?.data;
          setStates(Array.isArray(data) ? data : data?.states || []);
        })
        .catch((err) => {
          console.error('Error fetching states:', err);
          setStates([]);
        });
    } else {
      setStates([]);
    }
  }, [formData.countryId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'countryId' ? { stateId: '' } : {}),
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.userName.trim()) newErrors.userName = 'Username is required';
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';

    if (!formData.systemPassword) newErrors.systemPassword = 'Password is required';
    if (!formData.systemConfirmPassword) newErrors.systemConfirmPassword = 'Confirm password is required';
    if (
      formData.systemPassword &&
      formData.systemConfirmPassword &&
      formData.systemPassword !== formData.systemConfirmPassword
    ) {
      newErrors.systemConfirmPassword = 'Passwords do not match';
    }

    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email';
    }
    if (!formData.mobileNumber.trim() || !/^\+?\d{7,15}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = 'Invalid mobile number';
    }
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.countryId) newErrors.countryId = 'Country is required';
    if (!formData.stateId) newErrors.stateId = 'State is required';

    return newErrors;
  };

  const handleSubmit = async () => {
  const validationErrors = validate();
  setErrors(validationErrors);
  if (Object.keys(validationErrors).length > 0) return;

  setIsSubmitting(true);
  setStatusMessage({ type: '', text: '' });
  try {
    const res = await axios.post(`${API_BASE_URL}/public/userSignUp`, formData);

    const message = res.data; // backend returns a plain string

    if (res.status === 200 && message.toLowerCase().includes('success')) {
      setStatusMessage({ type: 'success', text: message });
      // optional auto-close after 2s
      // setTimeout(() => onClose(true), 2000);
    } else {
      setStatusMessage({ type: 'error', text: message || 'Failed to create user' });
    }
  } catch (err) {
    console.error('Error submitting user:', err);
    setStatusMessage({
      type: 'error',
      text: err.response?.data || 'Something went wrong. Please try again.',
    });
  } finally {
    setIsSubmitting(false);
  }
};


  // prevent closing on outside click
  const handleDialogClose = (event, reason) => {
    if (reason && reason === 'backdropClick') return;
    onClose(false);
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="md">
      <DialogTitle>Create User</DialogTitle>
      <DialogContent dividers>
        {statusMessage.text && (
          <Typography
            sx={{ mb: 2 }}
            color={statusMessage.type === 'error' ? 'error' : 'green'}
          >
            {statusMessage.text}
          </Typography>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Username"
              name="userName"
              value={formData.userName}
              onChange={handleChange}
              fullWidth
              error={!!errors.userName}
              helperText={errors.userName}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              fullWidth
              error={!!errors.firstName}
              helperText={errors.firstName}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Password"
              name="systemPassword"
              type="password"
              value={formData.systemPassword}
              onChange={handleChange}
              fullWidth
              error={!!errors.systemPassword}
              helperText={errors.systemPassword}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Confirm Password"
              name="systemConfirmPassword"
              type="password"
              value={formData.systemConfirmPassword}
              onChange={handleChange}
              fullWidth
              error={!!errors.systemConfirmPassword}
              helperText={errors.systemConfirmPassword}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              fullWidth
              error={!!errors.email}
              helperText={errors.email}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Mobile Number"
              name="mobileNumber"
              value={formData.mobileNumber}
              onChange={handleChange}
              fullWidth
              error={!!errors.mobileNumber}
              helperText={errors.mobileNumber}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
              error={!!errors.address}
              helperText={errors.address}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.countryId} required>
              <InputLabel>Country</InputLabel>
              <Select
                name="countryId"
                value={formData.countryId}
                onChange={handleChange}
              >
                {countries.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.countryName}
                  </MenuItem>
                ))}
              </Select>
              {errors.countryId && <FormHelperText>{errors.countryId}</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.stateId} required>
              <InputLabel>State</InputLabel>
              <Select
                name="stateId"
                value={formData.stateId}
                onChange={handleChange}
                disabled={!formData.countryId}
              >
                {states.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.stateName}
                  </MenuItem>
                ))}
              </Select>
              {errors.stateId && <FormHelperText>{errors.stateId}</FormHelperText>}
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Create User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserSignUp;
